import { useTranslation } from "react-i18next";
import { Address } from "../../../models/Address";
import { Client } from "../../../models/Client";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../../../state/store";
import { useEffect, useState } from "react";
import { Toast } from "../../Toast";
import { deleteClientAddress, fetchAddressesByClientId, needToLoadClientAddress, setClientMainAddress } from "../../../state/addresses/actions";
import { toast, ToastContainer } from "react-toastify";
import { ClientAddressTable } from "./ClientAddressTable";
import { ClientAddressModal } from "./ClientAddressModal";
import { DeleteClientAddressModal } from "./DeleteClientAddressModal";
import Button from "../../Button";
import classNames from "classnames";
import { SpinCircle } from "../../SpinCircle";
import { SetClientMainAddressModal } from "./SetClientMainAddressModal";
import { LoadingMask } from "../../LoadingMask";

interface Props {
  isNewClient: boolean;
  addresses?: Address[];
  client: Client | null;
  canEditClient: boolean;
  addressesHasChanged?: (addresses: Address[]) => void;
  submitNewAddress?: () => void;
}

export const ClientAddressForm = (props: Props) => {
    const { t } = useTranslation();
    const dispatch = useDispatch<AppDispatch>();

    const clientAddresses = useSelector((state: RootState) => state.addresses.addresses);
    const isLoadingAddresses = useSelector((state: RootState) => state.addresses.isLoading);
    const needLoadClientAddresses = useSelector((state: RootState) => state.addresses.needToLoadAddresses);
    const error = useSelector((state: any) => state.addresses.error);
    const [showAddressError, setShowAddressError] = useState(false);

    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [addresses, setAddress] = useState<Address[]>(props.addresses ? props.addresses : []);

    const [showClientAddressModal, setShowClientAddressModal] = useState<{
        show: boolean;
        update: boolean;
        address?: Address;
        index?: number;
        isNewClient: boolean;
        client?: Client;
        canEditClient: boolean;
        isFirstAddress: boolean;
    }>({
        show: false,
        update: false,
        isNewClient: props.isNewClient,
        canEditClient: props.canEditClient,
        isFirstAddress: false
    });
    
    const [ showDeleteClientAddressModal, setShowDeleteClientAddressModal] = useState<{
        address?: Address;
        show: boolean;
    }>({ show: false, address: undefined });

    const [ showSetMainAddressModal, setShowSetMainAddressModal] = useState<{
        address?: Address;
        show: boolean;
    }>({ show: false, address: undefined });
    
    useEffect(() => {
        if (showAddressError) {
          Toast(t(error?.reason ?? "SOMETHING_WENT_WRONG"), t(error?.cause_info));
        }
    }, [showAddressError, error, t]);

    useEffect(() => {
        //TODO: Review state and get data
        if (!props.isNewClient && props.client !== null) {
            if(needLoadClientAddresses) {
                dispatch(fetchAddressesByClientId({clientId: props.client?.id!})).then((e) => {
                    let addressess = (e.payload || []) as Address[];
                    setAddress(addressess);
                });
                dispatch(needToLoadClientAddress(false));
            } else {
                setAddress(clientAddresses);
            }
        }
    }, []);

    const addAdress = (address: Address) => {
        setAddress((addresses) => [...addresses, address]);
    }

    const updateAddress = (index: number, address: Address) => {
        setAddress((addresses) =>
            addresses.map((ele, i) => {
                if (i === index) { return address }
                return ele;
            })
        );
    }

    const removeAddress = (index: number) => {
        setAddress((value) => value.filter((_, i) => i !== index));
    }

    const deleteAddress = (address: Address) => {
        dispatch(deleteClientAddress({ clientId: props.client?.id!, addressId: address.id! })).then((e) => {
            if (e.type === "client/deleteClientAddress/rejected") {
                setShowAddressError(true);
            } else {
                toast(t("SUCCESSFULLY_DELETE_CLIENT_ADDRESS"));
                let addresses = (e.payload || []) as Address[];
                setAddress(addresses);
            }
        });
    }

    const onSetMainAddress = (address: Address) => {
        dispatch(setClientMainAddress({addressId: address.id!, clientId: props.client?.id!})).then((e) => {
            if (e.type === "client/setClientMainAddress/rejected") {
                setShowAddressError(true);
            } else {
                let addressess = (e.payload || []) as Address[];
                setAddress(addressess);
                toast(t("SUCCESSFULLY_SET_MAIN_ADDRESS"));
            }
        });
    };

    useEffect(() => {
        if (props.addressesHasChanged) {
            props.addressesHasChanged(addresses);
        }
    }, [addresses]);

    return (
        <>
            <ClientAddressTable
                isNewClient={props.isNewClient}
                addresses={addresses}
                canEditClient={props.canEditClient}
                addAdress={() =>
                    setShowClientAddressModal({
                        show: true,
                        update: false,
                        address: undefined,
                        client: props.client !== null ? props.client : undefined,
                        index: undefined,
                        canEditClient: props.canEditClient,
                        isFirstAddress: addresses.length === 0,
                        isNewClient: props.isNewClient
                    })
                }
                updateAddress={(index: number, address: Address) => 
                    setShowClientAddressModal({
                        show: true,
                        update: true,
                        address: address,
                        index: index,
                        client: props.client !== null ? props.client : undefined,
                        canEditClient: props.canEditClient,
                        isFirstAddress: addresses.length === 0,
                        isNewClient: props.isNewClient
                    })
                }
                removeAddress={(index) =>
                    props.isNewClient
                    ? removeAddress(index)
                    : setShowDeleteClientAddressModal({
                        address: addresses[index],
                        show: true
                        })
                }
                setMainAddress={(index: number, address: Address) => {
                    if(!props.isNewClient) {
                        setShowSetMainAddressModal({
                            address: address,
                            show: true
                        })
                    }
                }}
            />
            {props.isNewClient && (
                <div className="w-full flex items center justify-end px-3">
                    <Button onClick={() => props.submitNewAddress ? props.submitNewAddress() : null}
                        classNames="w-full text-lg font-semibold py-2 flex flex-row justify-center mx-3"
                        isDisabled={isLoading}
                    >
                        <div className={classNames({ "mr-2": isLoading })}>Save</div>
                        {isLoading && <SpinCircle />}
                    </Button>
                </div>
            )}
            <ClientAddressModal
                info={showClientAddressModal}
                onClose={() => setShowClientAddressModal({
                    show: false,
                    update: false,
                    address: undefined,
                    index: undefined,
                    canEditClient: props.canEditClient,
                    isFirstAddress: addresses.length === 0,
                    isNewClient: props.isNewClient
                })}
                addAddress={(address: Address) => addAdress(address)}
                updateAddress={(address: Address, index: number) => updateAddress(index, address)}
            />
            <DeleteClientAddressModal
                info={showDeleteClientAddressModal}
                onClose={() => setShowDeleteClientAddressModal({
                    address: undefined,
                    show: false
                })}
                deleteAddress={(address: Address) => deleteAddress(address)}
            />
            <SetClientMainAddressModal
                info={showSetMainAddressModal}
                onClose={() => setShowSetMainAddressModal({
                    show: false,
                    address: undefined
                })}
                setMainAddress={(address: Address) => onSetMainAddress(address)}
            />
            <ToastContainer progressStyle={{ background: "#D4AF37" }} />
            {isLoadingAddresses && <LoadingMask />}
        </>
    )
}